/**
 * Root Application Component
 * Sets up providers and router for the entire application
 */

import { useEffect, useState } from "react";
import { RouterProvider } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AuthProvider } from "./contexts/AuthContext";
import { ToastProvider } from "./components/ui/Toast";
import { router } from "./router";
import ErrorBoundary from "./components/ErrorBoundary";
import { initializeErrorHandlers } from "./utils/errorLogger";
import { registerServerErrorPersistence } from "./services/errorLog.service";
import { initializeMonitoring } from "./utils/monitoring";
import { useVersionCheck } from "./hooks/useVersionCheck";
import UpdateBanner from "./components/common/UpdateBanner";
import "./App.css";

// Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

function App() {
  const [showUpdateBanner, setShowUpdateBanner] = useState(false);
  const { updateAvailable, reloadApp } = useVersionCheck();

  // Initialize global error handlers and monitoring on mount
  useEffect(() => {
    registerServerErrorPersistence();
    initializeErrorHandlers();
    initializeMonitoring();
  }, []);

  // Show banner when update is available
  useEffect(() => {
    if (updateAvailable) {
      setShowUpdateBanner(true);
    }
  }, [updateAvailable]);

  const handleReload = async () => {
    await reloadApp();
  };

  const handleDismiss = () => {
    setShowUpdateBanner(false);
    // Auto-reload after 10 minutes if user dismissed banner
    setTimeout(
      async () => {
        await reloadApp();
      },
      10 * 60 * 1000,
    );
  };

  return (
    <ErrorBoundary>
      {showUpdateBanner && (
        <UpdateBanner onReload={handleReload} onDismiss={handleDismiss} />
      )}
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
